import { useAction, useAtom } from '@reatom/npm-react'
import styled from 'styled-components'
import { useMemo } from 'use-memo-one'

import { Button } from '@blade/ui-kit'
import { WalletAddress } from '@root/entities/wallet'
import { useTranslation } from '@root/shared/localization'
import { Trash2Icon, WarningTriangleIcon } from '@root/shared/ui-kit'

import { reatomDeleteWalletTracking } from '../../atoms/reatom-delete-wallet-tracking'
import { reatomToggleWalletAlert } from '../../atoms/reatom-edit-tracking-wallet-form'

import type { TrackingWallet } from '@root/entities/wallet-tracking'
import type { ClassNameProps } from '@root/shared/react'

type TrackingWalletsActionsProps = {
  wallet: TrackingWallet.TrackingWallet
} & ClassNameProps

export const TrackingWalletsActions: React.FC<TrackingWalletsActionsProps> = ({
  className,
  wallet,
}) => {
  const { t } = useTranslation()
  const toggleAlertModel = useMemo(
    () =>
      // eslint-disable-next-line @reatom/unit-naming-rule
      reatomToggleWalletAlert({
        walletAddress: WalletAddress(wallet.walletAddress),
      }),
    [wallet.walletAddress],
  )
  const [toggleAlertState] = useAtom(toggleAlertModel)
  const toggleAlert = useAction(toggleAlertModel.execute)
  const deleteModel = useMemo(
    () =>
      // eslint-disable-next-line @reatom/unit-naming-rule
      reatomDeleteWalletTracking({
        walletAddress: WalletAddress(wallet.walletAddress),
      }),
    [wallet.walletAddress],
  )
  const [deleteState] = useAtom(deleteModel)
  const deleteWallet = useAction(deleteModel.execute)

  return (
    <Root className={className}>
      <AlertButton
        radius="rounded"
        size="xxs"
        variant="soft"
        busy={toggleAlertState.loading}
        data-active={wallet.alertsEnabled}
        onClick={() => {
          toggleAlert(!wallet.alertsEnabled)
        }}
        iconLeft={<WarningTriangleIcon size={12} />}
      >
        {t('wallet-tracker.table.alert')}
      </AlertButton>
      <Button
        radius="rounded"
        size="xxs"
        variant="ghost"
        busy={deleteState.loading}
        onClick={() => deleteWallet()}
        iconLeft={<Trash2Icon />}
      />
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  grid-auto-flow: column;
  gap: 8px;
  align-items: center;
`

const AlertButton = styled(Button)`
  border-radius: 6px;
  height: 20px;

  font-size: 11px;
  font-weight: 500;
  line-height: 16px;

  background: var(--background-2);
  color: var(--text-8);

  &[data-active='true'] {
    background: var(--warningA-3);
    color: var(--warning-9);
    &:hover {
      background: var(--warningA-4);
    }
  }
`
